/**
 * SettingsApiKeysPage Component
 * API Keys management - EXACT mockup implementation
 * Only Azure AI Foundry configurations supported
 */

import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux";
import {
  fetchAllConfigs,
  deleteConfig,
} from "../redux/slices/llmConfigsSlice";
import { LLMConfiguration } from "../types/llm.types";
import { ConfirmationModal } from "../components/common/ConfirmationModal";
import { AzureConfigModal } from "../components/settings/AzureConfigModal";
import {
  Plus,
  Copy,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";

export const SettingsApiKeysPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { configs } = useAppSelector((state) => state.llmConfigs);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<LLMConfiguration | undefined>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<LLMConfiguration | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAllConfigs());
  }, [dispatch]);

  const handleCreateNew = () => {
    setEditingConfig(undefined);
    setModalOpen(true);
  };

  const handleEdit = (config: LLMConfiguration) => {
    setEditingConfig(config);
    setModalOpen(true);
  };

  const handleDelete = (config: LLMConfiguration) => {
    setConfigToDelete(config);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (configToDelete?.id) {
      try {
        await dispatch(deleteConfig(configToDelete.id)).unwrap();
        setDeleteModalOpen(false);
        setConfigToDelete(null);
      } catch (err) {
        console.error("Failed to delete:", err);
      }
    }
  };

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="flex-1 p-10 flex flex-col gap-8 bg-[#0d1425] min-h-screen">
      {/* Header */}
      <header className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">API Keys</h1>
          <p className="text-gray-400 text-sm">Manage your API access.</p>
        </div>
        <button 
          onClick={handleCreateNew}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-colors shadow-lg shadow-blue-600/20"
        >
          <Plus size={18} />
          Create New Key
        </button>
      </header>

      {/* Table Section */}
      <div className="bg-[#111a2e] rounded-xl border border-white/5 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/5 text-gray-400 text-xs uppercase tracking-wider">
              <th className="px-6 py-4 font-medium">Name</th>
              <th className="px-6 py-4 font-medium">Key</th>
              <th className="px-6 py-4 font-medium">Created Date</th>
              <th className="px-6 py-4 font-medium">Last Used</th>
              <th className="px-6 py-4 font-medium">Status</th>
              <th className="px-6 py-4 font-medium text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {configs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No API keys configured. Click "Create New Key" to add one.
                </td>
              </tr>
            ) : (
              configs.map((config: LLMConfiguration) => (
                <tr key={config.id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="px-6 py-5 text-sm font-medium text-gray-200">
                    {config.config_name || config.deployment_name || "Azure Configuration"}
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-3">
                      <code className="text-xs text-gray-400 font-mono">
                        {config.has_api_key ? "sk_live_... ••••••••••••" : "Not set"}
                      </code>
                      <div className="relative group/tooltip">
                        <button 
                          onClick={() => handleCopy(config.id, config.custom_endpoint || "")}
                          className="p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-gray-400 transition-colors"
                        >
                          <Copy size={14} />
                        </button>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black text-[10px] rounded opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap">
                          {copiedId === config.id ? "Copied!" : "Copy endpoint"}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-sm text-gray-400">
                    {formatDate(config.created_at)}
                  </td>
                  <td className="px-6 py-5 text-sm text-gray-400">
                    {formatDate(config.updated_at)}
                  </td>
                  <td className="px-6 py-5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${
                      config.has_api_key 
                        ? "bg-green-500/10 text-green-400 border border-green-500/20" 
                        : "bg-red-500/10 text-red-400 border border-red-500/20"
                    }`}>
                      {config.has_api_key ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                      {config.has_api_key ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex justify-center relative group/tooltip">
                      <button 
                        onClick={() => handleDelete(config)}
                        className="p-2 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black text-[10px] rounded opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap">
                        Delete key
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Bottom Section */}
      <div className="flex justify-between items-start gap-6">
        <a 
          href="https://learn.microsoft.com/en-us/azure/ai-services/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-400 text-sm font-medium hover:underline underline-offset-4"
        >
          API Documentation
        </a>

        {/* Usage Stats Card */}
        <div className="bg-[#111a2e] border border-white/5 rounded-xl p-6 w-80">
          <h3 className="text-sm font-semibold text-white mb-4">Usage Stats</h3>
          <div className="w-full bg-white/5 rounded-full h-2 mb-4 overflow-hidden">
            <div 
              className="bg-blue-500 h-full rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]"
              style={{ width: `${Math.min((configs.length / 10) * 100, 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-400">
            <span className="text-white font-bold">{configs.length}</span> / 10{" "}
            <span className="ml-1">API configurations active</span>
          </p>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="mt-auto bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4 flex items-center gap-3">
        <AlertCircle className="text-yellow-500 shrink-0" size={20} />
        <p className="text-sm text-yellow-200/80">
          <span className="font-bold text-yellow-500 mr-1">Warning:</span>
          Keep your API keys secure. Do not share them in public repositories or client-side code.
        </p>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <AzureConfigModal
          config={editingConfig}
          onClose={() => setModalOpen(false)}
          onSave={() => {
            setModalOpen(false);
            dispatch(fetchAllConfigs());
          }}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete API Key"
        message={`Are you sure you want to delete "${configToDelete?.config_name || configToDelete?.deployment_name || "this configuration"}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
};
